import React from 'react';
import { useTranslation } from 'react-i18next';
import CardInfo from './CardInfo';

/**
 * SynergyMap — pairwise correlation list, with top pair highlighted.
 * Reads `data.synergyMap` from /api/command/:concessionaireId.
 */
export default function SynergyMap({ map }) {
    const { t } = useTranslation();
    const pairs = Array.isArray(map?.pairs) ? map.pairs : [];
    const top = map?.topPair;
    const topKey = top ? `${top.a}|${top.b}` : null;

    return (
        <div className="v3-card v3-cc-card">
            <div className="v3-cc-card-header-row">
                <h3>{t('Synergy Map')}</h3>
                <CardInfo title={t('About Synergy Map')}>
                    <p>
                        {t('This card surfaces')} <strong>{t('tenant pairs whose sales move together')}</strong> — {t('useful for co-location, cross-promotion, and leasing-mix decisions.')}
                    </p>
                    <p>
                        <strong>{t('Correlation')}</strong> {t('is the Pearson correlation of the two tenants’ daily sales over the period, from −1 to +1. Values near +1 mean they rise and fall together.')}
                    </p>
                    <p>
                        <strong>{t('Top pair')}</strong> {t('is the highest-correlated pair and is highlighted at the top.')}
                    </p>
                    <p>
                        {t('The percentage shown is the estimated sales lift — how much one tenant’s presence is associated with uplift in the other.')}
                    </p>
                </CardInfo>
            </div>
            {top && (
                <div className="v3-cc-card-meta" style={{ marginBottom: 8 }}>
                    {t('Top pair')}: <strong style={{ color: 'var(--v3-teal)' }}>{top.a} ↔ {top.b}</strong>
                    {' '}· {t('corr')} {Number(top.correlation || 0).toFixed(2)}
                </div>
            )}
            {pairs.length === 0 ? (
                <div className="v3-text-muted" style={{ fontSize: 13 }}>{t('No correlation pairs computed.')}</div>
            ) : (
                <div className="v3-cc-synergy-list">
                    {pairs.map((p, i) => {
                        const key = `${p.a}|${p.b}`;
                        const isTop = topKey && key === topKey;
                        const corr = Number(p.correlation || 0);
                        const pct = Math.max(0, Math.min(1, Math.abs(corr))) * 100;
                        return (
                            <div key={i} className={`v3-cc-synergy-row ${isTop ? 'top' : ''}`}>
                                <span className="pair">{p.a} ↔ {p.b}</span>
                                <span className="bar"><div style={{ width: `${pct}%` }} /></span>
                                <span className="val">
                                    {corr.toFixed(2)}
                                    {p.lift_pct != null && <span style={{ opacity: 0.6 }}> · {Number(p.lift_pct).toFixed(0)}%</span>}
                                </span>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
